import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import ToastContext from "../contexts/ToastContext";
import PageHeader from "../components/PageHeader";
import "./MinhasInscricoes.css";
import { FaDownload } from "react-icons/fa";

const formatInscricaoId = (id) => id.toString().padStart(4, "0");

const isWithinWindow = (start, end) => {
  if (!start || !end) return false;
  const now = Date.now();
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  return now >= startMs && now <= endMs;
};

function MinhasInscricoes() {
  const [inscricoes, setInscricoes] = useState([]);
  const [deletingInscricaoId, setDeletingInscricaoId] = useState(null);
  const [confirmDeleteInscricao, setConfirmDeleteInscricao] = useState(null);
  const [agoraMs, setAgoraMs] = useState(Date.now());
  const { addToast } = useContext(ToastContext);
  const navigate = useNavigate();

  const fetchInscricoes = useCallback(async () => {
    try {
      const { data } = await api.get("/inscricoes/minhas-inscricoes");
      setInscricoes(data);
    } catch (error) {
      addToast("Erro ao buscar inscrições.", "error");
    }
  }, [addToast]);

  useEffect(() => {
    fetchInscricoes();
  }, [fetchInscricoes]);

  useEffect(() => {
    const timer = setInterval(() => {
      setAgoraMs(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const canManageInscricao = (inscricao) => {
    if (!inscricao?.data_inicio_inscricao || !inscricao?.data_fim_inscricao) {
      return false;
    }

    const startMs = new Date(inscricao.data_inicio_inscricao).getTime();
    const endMs = new Date(inscricao.data_fim_inscricao).getTime();
    return agoraMs >= startMs && agoraMs <= endMs;
  };

  const isRecursoWindowOpen = (inscricao) =>
    isWithinWindow(inscricao.data_inicio_recurso, inscricao.data_fim_recurso);

  const hasPontuacao = (inscricao) =>
    Boolean(inscricao.pontuacao_liberada) &&
    inscricao.pontuacao !== null && inscricao.pontuacao !== undefined;

  const handleOpenDeletePopup = (inscricao) => {
    if (!canManageInscricao(inscricao)) {
      addToast("Prazo encerrado. Não é possível excluir.", "error");
      return;
    }
    setConfirmDeleteInscricao(inscricao);
  };

  const handleCloseDeletePopup = () => {
    setConfirmDeleteInscricao(null);
  };

  const handleDelete = async () => {
    if (!confirmDeleteInscricao) return;
    const inscricao = confirmDeleteInscricao;
    if (!canManageInscricao(inscricao)) {
      addToast("Prazo encerrado. Não é possível excluir.", "error");
      handleCloseDeletePopup();
      return;
    }

    setDeletingInscricaoId(inscricao.id);
    try {
      await api.delete(`/inscricoes/${inscricao.id}`);
      addToast("Inscrição excluída com sucesso!", "success");
      handleCloseDeletePopup();
      await fetchInscricoes();
    } catch (error) {
      addToast(
        error.response?.data?.message || "Erro ao excluir inscrição.",
        "error"
      );
    } finally {
      setDeletingInscricaoId(null);
    }
  };

  const handleDownloadComprovante = async (inscricaoId) => {
    try {
      const response = await api.get(
        `/relatorios/inscricao/${inscricaoId}/comprovante`,
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `comprovante_inscricao_${inscricaoId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      addToast("Erro ao baixar comprovante.", "error");
    }
  };

  return (
    <>
      <PageHeader title="Minhas Inscrições" />
      <div className="inscricoes-list stack-lg">
        {inscricoes.length > 0 ? (
          inscricoes.map((inscricao) => (
            <div
              key={inscricao.id}
              className={`inscricao-card surface-section ${
                inscricao.invalidada ? "inscricao-card--invalidada" : ""
              }`}
            >
              <div className="inscricao-card-header">
                <h2>{`${inscricao.edital_titulo} - Edital Nº ${inscricao.numero_edital}/${inscricao.ano_edital}`}</h2>
                <span
                  className={`inscricao-window-badge ${
                    canManageInscricao(inscricao)
                      ? "inscricao-window-badge--open"
                      : "inscricao-window-badge--closed"
                  }`}
                >
                  {canManageInscricao(inscricao)
                    ? `Modificação disponível até ${new Date(
                        inscricao.data_fim_inscricao
                      ).toLocaleString()}`
                    : "Prazo para editar/excluir encerrado"}
                </span>
              </div>

              {inscricao.invalidada ? (
                <div className="inscricao-invalidada">
                  <strong>Inscrição invalidada</strong>
                  <div className="inscricao-invalidada__text">
                    {inscricao.justificativa_invalidacao ||
                      "O controlador/administrador invalidou esta inscrição."}
                  </div>
                </div>
              ) : null}

              <div className="inscricao-info-grid">
                <div className="inscricao-info-item">
                  <span className="inscricao-info-label">Nº de Inscrição</span>
                  <strong>{formatInscricaoId(inscricao.id)}</strong>
                </div>
                <div className="inscricao-info-item">
                  <span className="inscricao-info-label">Cargo</span>
                  <strong>{inscricao.nome_cargo}</strong>
                </div>
                <div className="inscricao-info-item">
                  <span className="inscricao-info-label">Status do Edital</span>
                  <strong>{inscricao.edital_status}</strong>
                </div>
                <div className="inscricao-info-item">
                  <span className="inscricao-info-label">Pontuação</span>
                  <strong>
                    {inscricao.pontuacao_liberada
                      ? inscricao.pontuacao ?? "Aguardando avaliação"
                      : "Aguardando divulgação"}
                  </strong>
                </div>
                <div className="inscricao-info-item inscricao-info-item--full">
                  <span className="inscricao-info-label">Data da Inscrição</span>
                  <strong>{new Date(inscricao.data_inscricao).toLocaleString()}</strong>
                </div>
              </div>

              <div className="inscricao-actions-section">
                <button
                  onClick={() => handleDownloadComprovante(inscricao.id)}
                  className="btn-modern"
                >
                  <FaDownload />
                  <span>Baixar Comprovante</span>
                </button>

                {canManageInscricao(inscricao) ? (
                  <div className="inscricao-actions-user">
                    <div className="inscricao-edit-row">
                      <button
                        className="btn-modern button-secondary"
                        onClick={() => navigate(`/minhas-inscricoes/${inscricao.id}/editar`)}
                        type="button"
                      >
                        <span>Editar documentos</span>
                      </button>
                      <button
                        className="btn-modern btn-danger"
                        onClick={() => handleOpenDeletePopup(inscricao)}
                        disabled={deletingInscricaoId === inscricao.id}
                        type="button"
                      >
                        <span>
                          {deletingInscricaoId === inscricao.id
                            ? "Excluindo..."
                            : "Excluir inscrição"}
                        </span>
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              {isRecursoWindowOpen(inscricao) ? (
                hasPontuacao(inscricao) ? (
                  inscricao.recurso_id ? (
                    <button
                      onClick={() => navigate(`/recursos/${inscricao.recurso_id}`)}
                      className="btn-modern button-secondary"
                    >
                      <span>Ver recurso</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(`/recurso/novo/${inscricao.id}`)}
                      className="btn-modern button-secondary"
                    >
                      <span>Abrir recurso</span>
                    </button>
                  )
                ) : (
                  <p className="inscricao-recurso-hint">
                    Período de recurso aberto, aguardando publicação da pontuação.
                  </p>
                )
              ) : null}
            </div>
          ))
        ) : (
          <p>Você ainda não se inscreveu em nenhum edital.</p>
        )}
      </div>

      {confirmDeleteInscricao ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-card surface-section">
            <h3>Atenção: excluir inscrição</h3>
            <p>
              Ao excluir esta inscrição, todos os documentos anexados também serão removidos
              e esta ação não pode ser desfeita.
            </p>
            <p>
              <strong>Inscrição:</strong> {formatInscricaoId(confirmDeleteInscricao.id)}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-modern"
                onClick={handleCloseDeletePopup}
                disabled={deletingInscricaoId === confirmDeleteInscricao.id}
              >
                <span>Cancelar</span>
              </button>
              <button
                type="button"
                className="btn-modern btn-danger"
                onClick={handleDelete}
                disabled={deletingInscricaoId === confirmDeleteInscricao.id}
              >
                <span>
                  {deletingInscricaoId === confirmDeleteInscricao.id
                    ? "Excluindo..."
                    : "Confirmar exclusão"}
                </span>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default MinhasInscricoes;
